import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin } from 'lucide-react';
import { useApiResource } from '../hooks/useApiResource';
import { DetailPageLayout, DetailPageState } from '../components/DetailPageLayout';
import { getEventTypeLabel } from '../lib/contentLabels';

export default function EventDetail() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const { data: event, loading, error } = useApiResource(`/events/${slug}`, { lang: i18n.language });

  const getLocalizedField = (item, field) => {
    if (i18n.language === 'en' && item[`${field}_en`]) {
      return item[`${field}_en`];
    }
    return item[field];
  };

  if (loading) {
    return <DetailPageState>Loading...</DetailPageState>;
  }

  if (!event) {
    return <DetailPageState>{error ? 'Failed to load event.' : 'Event not found.'}</DetailPageState>;
  }

  const title = getLocalizedField(event, 'title');
  const description = getLocalizedField(event, 'description');
  const location = getLocalizedField(event, 'location');
  const dateLabel = new Date(event.date).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ru-RU');
  const program = getLocalizedField(event, 'program') || [];
  const programTitle = i18n.language === 'en' ? 'Program' : 'Программа';
  const dayLabel = i18n.language === 'en' ? 'Day' : 'День';
  const eventType = getEventTypeLabel(event.type, i18n.language);

  return (
    <DetailPageLayout backTo="/events" backLabel={i18n.language === 'en' ? 'Back to events' : 'Назад к мероприятиям'}>
        <div className="glass detail-page-card">
          <div className="relative h-72 md:h-96">
            <img src={event.image_url} alt={title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          </div>
          <div className="p-8 md:p-10 space-y-6">
            <div className="flex flex-wrap items-center gap-4 text-white/70 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{dateLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{location}</span>
              </div>
              <span className="text-xs uppercase tracking-widest text-[#E11D2E]">{eventType}</span>
            </div>
            <h1 className="detail-page-title">{title}</h1>
            <p className="detail-page-body whitespace-pre-line">{description}</p>

            {program.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-white">{programTitle}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {program.map((slot, idx) => (
                    <div key={`${slot.title}-${idx}`} className="glass rounded-2xl p-4">
                      <div className="text-xs text-[#E11D2E] mb-2">{dayLabel} {slot.day}</div>
                      <div className="text-white font-semibold">{slot.title}</div>
                      <div className="text-white/60 text-sm">{slot.time}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
    </DetailPageLayout>
  );
}
